import type { Task } from "../../../shared/types";
import { CATEGORY_LABELS, PRIORITY_LABELS } from "../../../shared/types";

interface FocusModeProps {
  readonly task: Task;
  readonly onComplete: () => void;
  readonly onSkip: () => void;
  readonly onClose: () => void;
}

export function FocusMode({
  task,
  onComplete,
  onSkip,
  onClose,
}: FocusModeProps) {
  const completedSubtasks = task.subtasks.filter((s) => s.done).length;
  const totalSubtasks = task.subtasks.length;
  const hasSubtasks = totalSubtasks > 0;

  return (
    <div className="fixed inset-0 bg-surface z-30 flex flex-col items-center justify-center p-6" data-ph-no-capture>
      <button
        type="button"
        onClick={onClose}
        aria-label="Fermer"
        className="absolute top-6 right-6 min-h-12 min-w-12 flex items-center justify-center
          text-ink-muted hover:text-ink focus:outline-none focus:ring-2 focus:ring-accent
          font-medium text-sm"
      >
        Fermer
      </button>

      <div className="max-w-2xl w-full space-y-8 text-center">
        <div className="space-y-4">
          <h1 className="text-4xl font-bold text-ink break-words">
            {task.title}
          </h1>

          <div className="flex items-center justify-center gap-4 flex-wrap">
            <span className="px-3 py-1.5 text-sm font-medium bg-accent/10 text-accent">
              {PRIORITY_LABELS[task.priority]}
            </span>
            <span className="px-3 py-1.5 text-sm font-medium bg-surface-alt text-ink-muted">
              {CATEGORY_LABELS[task.category]}
            </span>
          </div>
        </div>

        {hasSubtasks && (
          <div className="bg-surface-alt p-6 space-y-2">
            <p className="text-sm font-medium text-ink-muted">
              Progrès des sous-tâches
            </p>
            <p className="text-2xl font-bold text-accent">
              {completedSubtasks} / {totalSubtasks}
            </p>
            <div className="w-full bg-border h-2 mt-3">
              <div
                className="bg-accent h-full transition-all"
                style={{
                  width: `${(completedSubtasks / totalSubtasks) * 100}%`,
                }}
              />
            </div>
          </div>
        )}

        {task.notes && (
          <div className="bg-surface-alt p-6 text-left">
            <p className="text-sm font-medium text-ink-muted mb-2">Notes</p>
            <p className="text-sm text-ink whitespace-pre-wrap">{task.notes}</p>
          </div>
        )}

        <div className="flex gap-3 justify-center flex-wrap pt-6">
          <button
            type="button"
            onClick={onSkip}
            className="min-h-12 px-6 py-2 font-medium border border-border
              bg-surface text-ink hover:bg-surface-alt focus:outline-none
              focus:ring-2 focus:ring-accent"
          >
            Passer
          </button>
          <button
            type="button"
            onClick={onComplete}
            className="min-h-12 px-6 py-2 font-bold bg-success text-white
              hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-accent"
          >
            Terminer
          </button>
        </div>
      </div>
    </div>
  );
}
